import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePartnerSelection } from '../core/PartnerContext';
import { PartnerChoices, partnerLabel } from './PartnerSelector';

/*
 * The partner choice for the collapsed sidebar. Folded up, the menu
 * still shows its icons — every area stays reachable, but the one
 * setting that decides what all of them show did not: it lived in the
 * menu head, which is gone with the labels.
 *
 * So it becomes an icon of its own, and since a dropdown hanging off a
 * 56px rail would be narrower than any partner name, the list opens in
 * a dialog — the same choice, in the room it needs.
 */
export function PartnerSelectorButton() {
  const { t } = useTranslation();
  const { partners, selectedPartner, allPartnersSelected, isLoading } =
    usePartnerSelection();

  const [isOpen, setIsOpen] = useState(false);
  const dialogRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    const handleCancel = (event) => {
      event.preventDefault();
      setIsOpen(false);
    };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [isOpen]);

  /* nothing to choose from: with a single partner the head shows a
     fixed label, and the rail has no room to show one */
  if (isLoading || partners.length < 2) return null;
  if (!selectedPartner && !allPartnersSelected) return null;

  const selectedLabel = allPartnersSelected
    ? t('partner.all')
    : partnerLabel(selectedPartner);
  const label = t('partner.selectCurrent', { partner: selectedLabel });

  const close = () => {
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        className="nav-partner-select"
        title={label}
        aria-label={label}
        aria-haspopup="dialog"
        onClick={() => setIsOpen(true)}
      />

      {isOpen && (
        <dialog
          ref={dialogRef}
          className="partner-dialog"
          aria-label={t('partner.select')}
        >
          <h2>{t('partner.select')}</h2>
          <PartnerChoices id="partner-dialog-listbox" onDone={close} />
        </dialog>
      )}
    </>
  );
}
